// Funções puras do envio direto ao Azure Blob (Put Blob / Put Block + Put Block List).
// Ficam fora de mediaUpload.ts para os testes não carregarem o authService (MSAL).

/** Até este tamanho o arquivo vai num único Put Blob (limite do serviço: 5000 MiB; 64 MiB é seguro p/ retry). */
export const SINGLE_PUT_MAX_BYTES = 64 * 1024 * 1024;
/** Tamanho de cada bloco no Put Block. */
export const BLOCK_SIZE_BYTES = 8 * 1024 * 1024;
/** Novas tentativas por bloco (além da primeira). */
export const BLOCK_MAX_RETRIES = 3;
/** Limite aceito pela API (POST /media/uploads). */
export const MEDIA_MAX_BYTES = 2 * 1024 * 1024 * 1024;

export type UploadMode = 'single' | 'blocks';

export const uploadModeFor = (size: number): UploadMode => (size <= SINGLE_PUT_MAX_BYTES ? 'single' : 'blocks');

export interface ChunkPlan {
  index: number;
  start: number;
  /** Exclusivo (como em `Blob.slice`). */
  end: number;
}

/** Divide `size` bytes em blocos de `blockSize` (o último pode ser menor). Arquivo vazio → []. */
export const planChunks = (size: number, blockSize: number = BLOCK_SIZE_BYTES): ChunkPlan[] => {
  if (!(size > 0) || !(blockSize > 0)) return [];
  const chunks: ChunkPlan[] = [];
  for (let index = 0, start = 0; start < size; index++, start += blockSize) {
    chunks.push({ index, start, end: Math.min(start + blockSize, size) });
  }
  return chunks;
};

/**
 * Id do bloco: base64 de um texto de largura FIXA (o Blob exige que todos os ids de um blob
 * tenham o mesmo tamanho antes da codificação). 6 dígitos cobrem 2 GB / 8 MB com folga.
 */
export const blockIdFor = (index: number): string => {
  const raw = `block-${String(index).padStart(6, '0')}`;
  return btoa(raw);
};

/** Acrescenta parâmetros à URL SAS (que já tem `?sv=…&sig=…`). */
export const appendQuery = (url: string, query: string): string =>
  `${url}${url.includes('?') ? '&' : '?'}${query}`;

/** URL do Put Block — o id em base64 precisa ir codificado (`+`, `/`, `=`). */
export const putBlockUrl = (sasUrl: string, blockId: string): string =>
  appendQuery(sasUrl, `comp=block&blockid=${encodeURIComponent(blockId)}`);

export const putBlockListUrl = (sasUrl: string): string => appendQuery(sasUrl, 'comp=blocklist');

/** Corpo XML do Put Block List com os ids na ordem. */
export const blockListXml = (blockIds: string[]): string =>
  `<?xml version="1.0" encoding="utf-8"?><BlockList>${blockIds
    .map((id) => `<Latest>${id}</Latest>`)
    .join('')}</BlockList>`;

const MIME_BY_EXTENSION: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  heic: 'image/heic',
  heif: 'image/heif',
  gif: 'image/gif',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  m4v: 'video/x-m4v',
  webm: 'video/webm',
  avi: 'video/x-msvideo',
  mkv: 'video/x-matroska',
};

/**
 * Content-Type do arquivo: o do navegador quando é imagem/vídeo; senão, pela extensão
 * (vários navegadores deixam `File.type` vazio para .heic/.mkv). '' se não for mídia aceita.
 */
export const guessContentType = (fileName: string, browserType?: string): string => {
  const type = (browserType ?? '').toLowerCase();
  if (type.startsWith('image/') || type.startsWith('video/')) return type;
  const ext = fileName.toLowerCase().split('.').pop() ?? '';
  return MIME_BY_EXTENSION[ext] ?? '';
};

/** Erro de cancelamento pelo usuário (não é falha). */
export class UploadCancelledError extends Error {
  constructor() {
    super('Envio cancelado');
    this.name = 'UploadCancelledError';
  }
}

export const isUploadCancelled = (err: unknown): boolean =>
  err instanceof UploadCancelledError || (err instanceof DOMException && err.name === 'AbortError');
